import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const AboutSection = styled.section`
  padding: 110px 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.3), transparent);
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2.25rem, 5vw, 3.25rem);
  font-weight: 800;
  margin-bottom: 3.75rem;
  background: linear-gradient(135deg, #ffffff 0%, #9beeff 58%, #7affc8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -0.7rem;
    left: 0;
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, #7affc8, rgba(103, 216, 255, 0));
  }
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const AboutContainer = styled.div`
  display: grid;
  grid-template-columns: 1.08fr 0.92fr;
  gap: 3.5rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const AboutText = styled(motion.div)`
  color: #cccccc;
  line-height: 2;
`;

const AboutParagraph = styled(motion.p)`
  font-size: 1.05rem;
  margin-bottom: 1.45rem;
  color: #aebdcb;
  font-weight: 400;
`;

const AboutHighlight = styled.span`
  color: #9beeff;
  font-weight: 700;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, #7affc8, transparent);
    opacity: 0.5;
  }
`;

const AboutImage = styled(motion.div)`
  width: 100%;
  height: 440px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.28);
  
  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 8px;
    padding: 2px;
    background: linear-gradient(135deg, #7affc8, #67d8ff);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    z-index: -1;
    opacity: 0.5;
  }
  
  @media (max-width: 768px) {
    height: 350px;
  }
`;

const ProfileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* 인물이 우측 하단에 있어 얼굴·상체가 보이도록 위치만 조정 */
  object-position: 100% 80%;
  transition: transform 0.5s ease;
  
  ${AboutImage}:hover & {
    transform: scale(1.05);
  }
`;

const SkillsPreview = styled(motion.div)`
  margin-top: 2.5rem;
`;

const SkillsTitle = styled.h3`
  font-size: 1.4rem;
  color: #ffffff;
  margin-bottom: 1.5rem;
  font-weight: 600;
`;

const SkillsList = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
`;

const SkillTag = styled(motion.li)`
  background: rgba(128, 230, 255, 0.08);
  color: #9beeff;
  padding: 0.6rem 1.2rem;
  border-radius: 999px;
  font-size: 0.9rem;
  border: 1px solid rgba(128, 230, 255, 0.17);
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: default;
  
  &:hover {
    background: rgba(128, 230, 255, 0.16);
    border-color: rgba(128, 230, 255, 0.45);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 212, 255, 0.3);
  }
`;

const About: React.FC = () => {
  const skills = ['Spring Boot', 'Java', 'React', 'Flutter', 'Python', 'MySQL', 'AWS', 'TypeScript'];

  return (
    <AboutSection id="about">
      <SectionTitle
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        About Me
      </SectionTitle>
      
      <AboutContainer>
        <AboutText
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <AboutParagraph
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            안녕하세요! 저는 <AboutHighlight>풀스택 개발자</AboutHighlight>로, 
            화면부터 API·DB까지 한 흐름으로 맞춰 붙이는 일을 주로 합니다. 
            멋진 카피보다 실제로 쓰일 때 덜 꼬이는 흐름을 설계하는 데 더 신경 씁니다.
          </AboutParagraph>
          
          <AboutParagraph
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <AboutHighlight>약 4년간</AboutHighlight> Spring·React를 중심으로 웹을 만들었고, 
            모바일은 Flutter, 데이터는 MySQL, 배포는 AWS 쪽까지 필요할 때 손댑니다. 
            새 도구는 튜토리얼만 돌리기보다 작은 범위에 먼저 얹어 본 뒤 본 작업에 옮기는 편입니다.
          </AboutParagraph>
          
          <AboutParagraph
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            혼자 끝까지 밀어붙이는 것도 괜찮지만, 리뷰와 질문으로 갈등을 줄이는 과정에서 
            더 배운 적이 많습니다. &quot;내가 아니어도 고칠 수 있나&quot;를 기준으로 구조와 이름을 잡습니다.
          </AboutParagraph>
          
          <SkillsPreview
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <SkillsTitle>주요 기술 스택</SkillsTitle>
            <SkillsList>
              {skills.map((skill, index) => (
                <SkillTag 
                  key={index}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.1 }}
                >
                  {skill}
                </SkillTag>
              ))}
            </SkillsList>
          </SkillsPreview>
        </AboutText>
        
        <AboutImage
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
          whileHover={{ scale: 1.02 }}
        >
          <ProfileImage src="/IMG_3228.jpeg" alt="김수빈 프로필 사진" />
        </AboutImage>
      </AboutContainer>
    </AboutSection>
  );
};

export default About; 
